import React, { useState, useEffect, useCallback } from 'react'
import ResponsiveModal from '../shared/ResponsiveModal'
import InlineSvg from '../shared/InlineSvg'
import trashIconRaw from '../../../assets/images/dashboard/icon-trash.svg?raw'

const DELETE_BTN_BASE =
  'cursor-pointer focus:outline-none rounded-full font-inter font-semibold text-white border-0 disabled:opacity-60 disabled:cursor-not-allowed'

// WOP-4868 3.1: variante opcional del botón de acción (pill teal claro del Figma 5236-8835, baja de
// seguro). Sin variante el botón queda idéntico al actual (coral relleno, texto blanco).
const DELETE_BTN_TEAL =
  'cursor-pointer focus:outline-none rounded-full font-inter font-semibold text-woperty-teal bg-woperty-teal-light border border-woperty-teal disabled:opacity-60 disabled:cursor-not-allowed'

export default function DeleteModal() {
  const [isOpen,     setIsOpen]     = useState(false)
  const [deleteUrl,  setDeleteUrl]  = useState(null)
  const [title,      setTitle]      = useState('')
  const [body,       setBody]       = useState('')
  const [buttonText, setButtonText] = useState('Eliminar')
  const [ajaxMode,   setAjaxMode]   = useState(false)
  const [httpMethod, setHttpMethod] = useState('GET')
  const [acceptJson, setAcceptJson] = useState(false)
  const [loading,    setLoading]    = useState(false)
  // `blocked`: modo informativo, la acción no está permitida → botón deshabilitado.
  const [blocked,    setBlocked]    = useState(false)
  // Acción secundaria opcional (ej. late_fee: "seguir cobrando" vs "dejar de cobrar").
  const [secondaryUrl,  setSecondaryUrl]  = useState(null)
  const [secondaryText, setSecondaryText] = useState(null)
  // Estilo opcional del botón de acción (ej. 'teal' para la baja de seguro). Sin esto: coral relleno.
  const [buttonVariant, setButtonVariant] = useState(null)

  useEffect(() => {
    function handleOpen(e) {
      setDeleteUrl(e.detail.url)
      // `title` es el nuevo nombre; `message` se mantiene por compatibilidad.
      setTitle(e.detail.title || e.detail.message || '')
      setBody(e.detail.body || '')
      setButtonText(e.detail.buttonText || 'Eliminar')
      setAjaxMode(e.detail.ajax        || false)
      setHttpMethod(e.detail.method    || 'GET')
      setAcceptJson(e.detail.json      || false)
      setBlocked(e.detail.blocked      || false)
      setSecondaryUrl(e.detail.secondaryUrl     || null)
      setSecondaryText(e.detail.secondaryButton || null)
      setButtonVariant(e.detail.buttonVariant   || null)
      setLoading(false)
      setIsOpen(true)
    }
    window.addEventListener('woperty:openDeleteModal', handleOpen)
    return () => window.removeEventListener('woperty:openDeleteModal', handleOpen)
  }, [])

  const handleClose = useCallback(() => { setIsOpen(false); setLoading(false) }, [])

  // Botón de acción primario: teal claro con la variante, coral relleno por defecto (sin cambios).
  const isTeal       = buttonVariant === 'teal'
  const primaryClass = isTeal ? DELETE_BTN_TEAL : DELETE_BTN_BASE
  const primaryStyle = isTeal ? undefined : { backgroundColor: '#F28B82' }

  function runDelete(url) {
    if (blocked) return
    if (ajaxMode) {
      setLoading(true)
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const headers = { 'X-CSRF-Token': csrf, 'X-Requested-With': 'XMLHttpRequest' }
      if (acceptJson) headers.Accept = 'application/json'
      fetch(url, { method: httpMethod, headers })
        .then(res => (acceptJson ? res.json().catch(() => null) : null))
        .then(data => {
          window.dispatchEvent(new CustomEvent('woperty:deleteSuccess', { detail: { url, data } }))
          handleClose()
        })
        .catch(() => handleClose())
      return
    }

    const csrf      = document.querySelector('meta[name="csrf-token"]')?.content
    const form      = document.createElement('form')
    form.method     = 'POST'
    form.action     = url

    const methodInput  = document.createElement('input')
    methodInput.type   = 'hidden'
    methodInput.name   = '_method'
    methodInput.value  = 'DELETE'

    const csrfInput    = document.createElement('input')
    csrfInput.type     = 'hidden'
    csrfInput.name     = 'authenticity_token'
    csrfInput.value    = csrf || ''

    form.appendChild(methodInput)
    form.appendChild(csrfInput)
    document.body.appendChild(form)
    form.submit()
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={handleClose} maxWidthClass="max-w-[320px]">
      {isDesktop => (
        <>
          <div className={`flex flex-col items-center gap-3 ${isDesktop ? 'px-8 pt-0 pb-4' : 'px-12 pt-2 pb-6'}`}>
            <InlineSvg svg={trashIconRaw} className={`w-8 h-8 ${loading ? 'animate-spin' : ''}`} />
            {title && (
              <p className={`font-sans font-medium text-woperty-dark text-center leading-snug ${isDesktop ? 'text-[1.1rem]' : 'text-[1.2rem] max-w-[20rem]'}`}
                 dangerouslySetInnerHTML={{ __html: title }} />
            )}
            {body && (
              <p className={`font-inter text-[13px] text-woperty-text-muted text-center leading-snug ${isDesktop ? '' : 'max-w-[20rem]'}`}
                 dangerouslySetInnerHTML={{ __html: body }} />
            )}
          </div>

          {secondaryUrl ? (
            <div className={`flex flex-col items-center gap-3 ${isDesktop ? 'pb-5' : 'px-12 pb-6'}`}>
              <button
                onClick={() => runDelete(deleteUrl)}
                disabled={loading || blocked}
                className={`${primaryClass} ${isDesktop ? 'px-5 py-2.5 text-[13px]' : 'w-full py-2.5 text-[12px]'}`}
                style={primaryStyle}
              >
                {buttonText}
              </button>
              <button
                onClick={() => runDelete(secondaryUrl)}
                disabled={loading || blocked}
                className={`${DELETE_BTN_BASE} ${isDesktop ? 'px-5 py-2.5 text-[13px]' : 'w-full py-2.5 text-[12px]'}`}
                style={{ backgroundColor: '#F28B82' }}
              >
                {secondaryText}
              </button>
            </div>
          ) : (
            <div className={isDesktop ? 'mx-auto pb-5' : 'flex px-28 py-4'}>
              <button
                onClick={() => runDelete(deleteUrl)}
                disabled={loading || blocked}
                className={`${primaryClass} ${isDesktop ? 'px-5 py-2.5 text-[13px]' : 'flex-1 py-2.5 text-[11px]'}`}
                style={primaryStyle}
              >
                {buttonText}
              </button>
            </div>
          )}
        </>
      )}
    </ResponsiveModal>
  )
}
